/**
 * TanStack Query hooks for the LTV-predict App-Settings surface (WS12).
 * Backs the subscription-type / custom-metrics / subscriptions / event-mapping
 * sub-tabs. Mirrors the /api/app-settings/* LTV endpoints (WS12a + WS12b).
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "./api";

// ---- types (mirror app/schemas/ltv.py) --------------------------------------
export type ClassificationMethod = "price_split" | "subscription_id";

export type AlgorithmRole =
  | "trial_start"
  | "trial_activated"
  | "subscription_started_direct"
  | "renewal"
  | "trial_cancel"
  | "subscription_cancel"
  | "expiration"
  | "billing_issue"
  | "product_change"
  | "lifetime_purchase"
  | "ignore";

export const LTV_ROLES: AlgorithmRole[] = [
  "trial_start", "trial_activated", "subscription_started_direct", "renewal",
  "trial_cancel", "subscription_cancel", "expiration", "billing_issue",
  "product_change", "lifetime_purchase", "ignore",
];
/** The 10 funnel roles selectable in a custom metric (everything but ignore). */
export const LTV_FUNNEL_ROLES: AlgorithmRole[] = LTV_ROLES.filter(
  (r) => r !== "ignore",
);

export interface SubscriptionConfig {
  classification_method: ClassificationMethod;
  split_currency: string;
  adjust_go_to_goal_enabled: boolean;
  subscription_type_detection_enabled: boolean;
  auto_coefficient_learning_enabled: boolean;
  default_vat_pct: number;
  min_payers_threshold: number;
  ci_threshold_pct: number;
  divergence_threshold_pct: number;
  learning_min_events_threshold: number;
  min_trials_for_factual_conv: number;
  tail_handoff_week: number;
  min_alive_for_own_tail: number;
  max_censoring_pct: number;
}

export type SubscriptionConfigUpdate = Partial<
  Pick<SubscriptionConfig, "classification_method" | "split_currency" | "default_vat_pct">
>;

export type SubscriptionAdvancedUpdate = Partial<
  Omit<
    SubscriptionConfig,
    "classification_method" | "split_currency" | "default_vat_pct"
  >
>;

export interface EventMappingRow {
  provider: string;
  provider_event_name: string;
  algorithm_event_role: AlgorithmRole;
}

export interface CustomMetric {
  metric_key: string;
  display_name: string;
  counted_event_roles: AlgorithmRole[];
  observation_window_days: number;
  prediction_day: number;
  // Present on the org-wide list (rule builder) for labelling / readiness;
  // null on the per-app App Settings list.
  app_id?: number | null;
  app_name?: string | null;
}

export interface CustomMetricReadiness {
  ready: boolean;
  missing: string[];
}

export interface PriceMappingRow {
  billing_period: string;
  has_trial: boolean;
  price_min: number;
  price_max: number;
  trial_duration_days: number | null;
}

export interface CommissionConfig {
  sbp_enrolled: boolean;
  sbp_entry_date: string | null;
  sbp_exit_date: string | null;
  external_payment_enabled: boolean;
}

export interface SubscriptionPrice {
  subscription_ref: string;
  country_code: string;
  price_usd: number;
}

export type CoefficientSource = "loaded" | "factual" | "predict";

export type CoefficientType = "renewal" | "trial_to_paid";

export interface CoefficientRow {
  coefficient_type: CoefficientType;
  segment_level: "country" | "keyword";
  country: string;
  keyword_text: string | null;
  from_day: number;
  goal_day: number;
  loaded_coefficient: number | null;
  factual_coefficient: number | null;
  factual_matured_to_day: number | null;
  factual_complete: boolean;
  predict_coefficient: number | null;
  predict_extrapolated: boolean;
  chosen_source: CoefficientSource;
  chosen_auto: boolean;
  flag: "switch_candidate" | "segment_matured" | null;
}

export interface ChosenSourcePick {
  coefficient_type?: CoefficientType;
  segment_level: "country" | "keyword";
  country: string;
  keyword_text: string | null;
  from_day: number;
  goal_day: number;
  chosen_source: CoefficientSource;
}

export interface LoadedCoefficientRow {
  coefficient_type: "renewal" | "trial_to_paid";
  billing_period: string | null;
  has_trial: boolean | null;
  segment_level: "keyword" | "country" | "global";
  country: string | null;
  keyword_text: string | null;
  from_day: number;
  to_day: number;
  coefficient: number;
}

const BASE = "/api/app-settings";
const enabledFor = (appId: number | null | undefined) => appId != null;

// ---- subscription config ----------------------------------------------------
export function useSubscriptionConfig(appId: number | null | undefined) {
  return useQuery({
    queryKey: ["ltv-subscription-config", appId ?? null],
    enabled: enabledFor(appId),
    queryFn: async () => {
      const { data } = await api.get<SubscriptionConfig>(
        `${BASE}/subscription-config`,
        { params: { app_id: appId } },
      );
      return data;
    },
    staleTime: 60_000,
  });
}

export function useUpdateSubscriptionConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { app_id: number } & SubscriptionConfigUpdate) => {
      const { app_id, ...body } = args;
      const { data } = await api.put<SubscriptionConfig>(
        `${BASE}/subscription-config`,
        body,
        { params: { app_id } },
      );
      return data;
    },
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: ["ltv-subscription-config", v.app_id] }),
  });
}

export function useUpdateSubscriptionAdvanced() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { app_id: number } & SubscriptionAdvancedUpdate) => {
      const { app_id, ...body } = args;
      const { data } = await api.put<SubscriptionConfig>(
        `${BASE}/subscription-config/advanced`,
        body,
        { params: { app_id } },
      );
      return data;
    },
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: ["ltv-subscription-config", v.app_id] }),
  });
}

// ---- event mapping ----------------------------------------------------------
export function useSubscriptionEventMapping(appId: number | null | undefined) {
  return useQuery({
    queryKey: ["ltv-event-mapping", appId ?? null],
    enabled: enabledFor(appId),
    queryFn: async () => {
      const { data } = await api.get<{ rows: EventMappingRow[] }>(
        `${BASE}/subscription-event-mapping`,
        { params: { app_id: appId } },
      );
      return data.rows;
    },
    staleTime: 60_000,
  });
}

export function useReplaceEventMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { app_id: number; rows: EventMappingRow[] }) => {
      const { data } = await api.put<{ rows: EventMappingRow[] }>(
        `${BASE}/subscription-event-mapping`,
        args,
      );
      return data.rows;
    },
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: ["ltv-event-mapping", v.app_id] }),
  });
}

// ---- custom metrics ---------------------------------------------------------
export function useCustomMetrics(appId: number | null | undefined) {
  return useQuery({
    queryKey: ["ltv-custom-metrics", appId ?? null],
    enabled: enabledFor(appId),
    queryFn: async () => {
      const { data } = await api.get<{ rows: CustomMetric[] }>(
        `${BASE}/custom-metrics`,
        { params: { app_id: appId } },
      );
      return data.rows;
    },
    staleTime: 60_000,
  });
}

// Org-wide custom metrics across every app the user can see, each tagged with
// app_id + app_name. The rule-condition builder uses this because a rule is
// app-abstract at creation (no app picker), so it must offer every app's
// metrics — same scoping as event types.
export function useAllCustomMetrics() {
  return useQuery({
    queryKey: ["ltv-custom-metrics", "org-all"],
    queryFn: async () => {
      const { data } = await api.get<{ rows: CustomMetric[] }>(
        `${BASE}/custom-metrics`,
      );
      return data.rows;
    },
    staleTime: 60_000,
  });
}

export function useUpsertCustomMetric() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { app_id: number } & CustomMetric) => {
      const { data } = await api.put<CustomMetric>(`${BASE}/custom-metrics`, args);
      return data;
    },
    onSuccess: (_d, v) => {
      qc.invalidateQueries({ queryKey: ["ltv-custom-metrics", v.app_id] });
      qc.invalidateQueries({ queryKey: ["ltv-metric-readiness", v.app_id] });
    },
  });
}

export function useDeleteCustomMetric() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { app_id: number; metric_key: string }) => {
      await api.delete(`${BASE}/custom-metrics/${encodeURIComponent(args.metric_key)}`, {
        params: { app_id: args.app_id },
      });
      return args;
    },
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: ["ltv-custom-metrics", v.app_id] }),
  });
}

export function useCustomMetricReadiness(
  appId: number | null | undefined,
  metricKey: string | null | undefined,
) {
  return useQuery({
    queryKey: ["ltv-metric-readiness", appId ?? null, metricKey ?? null],
    enabled: enabledFor(appId) && !!metricKey,
    queryFn: async () => {
      const { data } = await api.get<CustomMetricReadiness>(
        `${BASE}/custom-metrics/${encodeURIComponent(metricKey as string)}/readiness`,
        { params: { app_id: appId } },
      );
      return data;
    },
    staleTime: 30_000,
  });
}

// ---- price mapping ----------------------------------------------------------
export function usePriceMapping(appId: number | null | undefined) {
  return useQuery({
    queryKey: ["ltv-price-mapping", appId ?? null],
    enabled: enabledFor(appId),
    queryFn: async () => {
      const { data } = await api.get<{ rows: PriceMappingRow[] }>(
        `${BASE}/price-mapping`,
        { params: { app_id: appId } },
      );
      return data.rows;
    },
    staleTime: 60_000,
  });
}

export function useReplacePriceMapping() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { app_id: number; rows: PriceMappingRow[] }) => {
      const { data } = await api.put<{ rows: PriceMappingRow[] }>(
        `${BASE}/price-mapping`,
        args,
      );
      return data.rows;
    },
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: ["ltv-price-mapping", v.app_id] }),
  });
}

// ---- commission / SBP -------------------------------------------------------
export function useCommissionConfig(appId: number | null | undefined) {
  return useQuery({
    queryKey: ["ltv-commission-config", appId ?? null],
    enabled: enabledFor(appId),
    queryFn: async () => {
      const { data } = await api.get<CommissionConfig>(
        `${BASE}/commission-config`,
        { params: { app_id: appId } },
      );
      return data;
    },
    staleTime: 60_000,
  });
}

export function useUpdateCommissionConfig() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { app_id: number } & Partial<CommissionConfig>) => {
      const { app_id, ...body } = args;
      const { data } = await api.put<CommissionConfig>(
        `${BASE}/commission-config`,
        body,
        { params: { app_id } },
      );
      return data;
    },
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: ["ltv-commission-config", v.app_id] }),
  });
}

// ---- retail subscription prices --------------------------------------------
export function useSubscriptionPrices(appId: number | null | undefined) {
  return useQuery({
    queryKey: ["ltv-subscription-prices", appId ?? null],
    enabled: enabledFor(appId),
    queryFn: async () => {
      const { data } = await api.get<{ rows: SubscriptionPrice[] }>(
        `${BASE}/subscription-prices`,
        { params: { app_id: appId } },
      );
      return data.rows;
    },
    staleTime: 60_000,
  });
}

export function useUpsertSubscriptionPrice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { app_id: number } & SubscriptionPrice) => {
      const { data } = await api.put<{ rows: SubscriptionPrice[] }>(
        `${BASE}/subscription-prices`,
        args,
      );
      return data.rows;
    },
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: ["ltv-subscription-prices", v.app_id] }),
  });
}

export function useDeleteSubscriptionPrice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      app_id: number;
      subscription_ref: string;
      country_code: string;
    }) => {
      const { data } = await api.delete<{ rows: SubscriptionPrice[] }>(
        `${BASE}/subscription-prices`,
        { params: args },
      );
      return data.rows;
    },
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: ["ltv-subscription-prices", v.app_id] }),
  });
}

// ---- loaded coefficients ----------------------------------------------------
export function useLoadedCoefficients(appId: number | null | undefined) {
  return useQuery({
    queryKey: ["ltv-loaded-coefficients", appId ?? null],
    enabled: enabledFor(appId),
    queryFn: async () => {
      const { data } = await api.get<{ rows: LoadedCoefficientRow[]; total: number }>(
        `${BASE}/loaded-coefficients`,
        { params: { app_id: appId } },
      );
      return data;
    },
    staleTime: 60_000,
  });
}

export function useUploadLoadedCoefficients() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: {
      app_id: number;
      replace: boolean;
      rows: LoadedCoefficientRow[];
    }) => {
      const { data } = await api.post<{ inserted: number; total: number }>(
        `${BASE}/loaded-coefficients`,
        args,
      );
      return data;
    },
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: ["ltv-loaded-coefficients", v.app_id] }),
  });
}

// ---- three-row model (loaded / factual / predict) --------------------------
export function useCoefficientRows(appId: number | null | undefined) {
  return useQuery({
    queryKey: ["ltv-coefficient-rows", appId ?? null],
    enabled: enabledFor(appId),
    queryFn: async () => {
      const { data } = await api.get<{ rows: CoefficientRow[] }>(
        `${BASE}/coefficients`,
        { params: { app_id: appId } },
      );
      return data.rows;
    },
    staleTime: 60_000,
  });
}

export interface SubtypeBreakdownRow {
  subtype_key: string;
  billing_period: string;
  has_trial: boolean;
  payers: number;
  share_pct: number;
  loaded_coefficient: number | null;
  factual_coefficient: number | null;
  factual_matured_to_day: number | null;
  predict_coefficient: number | null;
  predict_extrapolated: boolean;
}

/** §4A subtype breakdown drill-in for one Coefficients segment-point. */
export function useSubtypeBreakdown(
  appId: number | null | undefined,
  seg: {
    segment_level: string;
    country: string;
    keyword_text: string | null;
    from_day: number;
    goal_day: number;
  } | null,
) {
  return useQuery({
    queryKey: ["ltv-subtype-breakdown", appId ?? null, seg],
    enabled: enabledFor(appId) && seg != null,
    queryFn: async () => {
      const { data } = await api.get<{ rows: SubtypeBreakdownRow[] }>(
        `${BASE}/coefficients/subtypes`,
        {
          params: {
            app_id: appId,
            segment_level: seg!.segment_level,
            country: seg!.country,
            keyword_text: seg!.keyword_text ?? undefined,
            from_day: seg!.from_day,
            goal_day: seg!.goal_day,
          },
        },
      );
      return data.rows;
    },
    staleTime: 60_000,
  });
}

export function useSetChosenSource() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (args: { app_id: number; picks: ChosenSourcePick[] }) => {
      const { data } = await api.patch<{ rows: CoefficientRow[] }>(
        `${BASE}/coefficients/source`,
        args,
      );
      return data.rows;
    },
    onSuccess: (_d, v) =>
      qc.invalidateQueries({ queryKey: ["ltv-coefficient-rows", v.app_id] }),
  });
}
